import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import { trackEvent } from '../utils/track';

/* ── Design tokens ── */
const CYAN = '#00D2C8';
const PURPLE = '#8C50FF';

const OKX_LINK   = process.env.REACT_APP_OKX_AFFILIATE_LINK   || 'https://my.okx.com/de/campaigns/web-three-kurzus?channelId=72518582';
const BYBIT_LINK = process.env.REACT_APP_BYBIT_AFFILIATE_LINK || 'https://www.bybit.eu/hu-EU/promo/campaign/EU_affiliate_depositcampaign/?affiliate_id=83241';
const DISCORD_LINK = 'https://discord.com/channels/999732072677658645';
const COURSE_LINK  = 'https://egyetem.web3kurzus.hu';

/* MiCA-licensed EU entities only. Bitget EU, Bitget.com, Bybit.com and
   Pionex were removed: they are global entities that may not serve EU
   customers (Bitget has no EU entity at all, and pionex.com is on the AMF
   blacklist), so recording a registration there contradicts the compliance
   the whole product is positioned on.

   This list is kept in sync BY HAND with the landing repo
   (web3kurzus-landing src/config/exchanges.js → activationExchanges()).
   Both write to the same affiliate_requests table, so they must not drift.
   Moving them onto one shared config is a separate piece of work. */
/* Statuses that mean "submitted, waiting for us" (manual_review: the exchange
   API could not confirm the UID). Same as the landing modal. */
export const WAITING = new Set(['pending', 'manual_review']);

/* "Általában 5 napon belül": manual approvals 2026-07…10 — median ≈ 2.2
   days, 80% within ≈ 4.5 days (33 requests). Same number as the landing. */
const APPROVAL_DAYS = 5;

const EXCHANGES = [
  { id: 'OKX Europe', label: 'OKX Europe' },
  { id: 'Bybit EU',   label: 'Bybit EU' },
];

const UID_GUIDE = {
  'OKX Europe': 'OKX: nyisd meg a profilod → Fiókadatok → ott találod a UID számot.',
  'Bybit EU':   'Bybit: kattints a profil ikonra (jobb felül) — a neved alatti szám a UID-d.',
};

/* Deliberately carries NO campaign figures. This repo has no copy of the
   exchange config, so any amount hardcoded here goes stale the moment a
   campaign changes — and it did: these tiles advertised "20 USDC" and
   "300 € BTC" long after both offers were replaced.

   Names and links are stable; the numbers live on web3kurzus.hu/bonusz,
   which is generated from src/config/exchanges.js in the landing repo. One
   click gets the user to the current terms, and this file can no longer be
   wrong. Sharing that config across both repos is still the right end state,
   it is just no longer urgent. */
const BONUSZ_URL = 'https://web3kurzus.hu/bonusz';

const PROMO_EXCHANGES = [
  { name: 'Bybit EU',   link: BYBIT_LINK, color: '#f59e0b', emoji: '🟡' },
  { name: 'OKX Europe', link: OKX_LINK,   color: CYAN,      emoji: '⚫' },
];


export default function TagActivationModal({ isOpen, onClose, source = 'unknown', onActivated, user, tier }) {
  const email = user?.email || '';

  const [exchange, setExchange] = useState('');
  const [uid, setUid] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUidHelp, setShowUidHelp] = useState(false);
  // 'checking' | 'form' | 'approved' | 'pending'
  const [phase, setPhase] = useState('checking');
  const [submittedAt, setSubmittedAt] = useState(null);
  // TAG-tiltásnál (verified_via 'individual') semleges üzenet, nem a szokásos határidő
  const [individual, setIndividual] = useState(false);

  /* On open the latest request decides what the user sees (same rule as the
     landing modal): waiting → pending screen with the submission date,
     approved and TAG/PRO today → done screen, anything else → the form.
     Previously the form always came back and a second UID could be sent. */
  useEffect(() => {
    if (!isOpen) return;
    trackEvent('activation_modal_opened', { source }, user);
    setError('');
    if (!user?.id) { setPhase('form'); return; }
    setPhase('checking');
    let cancelled = false;
    (async () => {
      let latest = null;
      try {
        const { data } = await supabase
          .from('affiliate_requests')
          .select('status, created_at, verified_via')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
        latest = data?.[0] || null;
      } catch { /* lookup failed → the form, as before */ }
      if (cancelled) return;
      if (latest && WAITING.has(latest.status)) {
        setSubmittedAt(latest.created_at);
        setIndividual(latest.verified_via === 'individual');
        setPhase('pending');
        trackEvent('activation_modal_existing', { source, state: 'pending' }, user);
      } else if (latest?.status === 'approved' && (tier === 'affiliate' || tier === 'pro')) {
        setPhase('approved');
        trackEvent('activation_modal_existing', { source, state: 'approved' }, user);
      } else {
        setPhase('form');
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, source, user, tier]);

  // Close on Escape
  const handleKey = useCallback((e) => { if (e.key === 'Escape') onClose?.(); }, [onClose]);
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleKey]);

  if (!isOpen) return null;

  const submit = async () => {
    if (!exchange) { setError('Válassz tőzsdét!'); return; }
    if (!uid.trim()) { setError('Add meg a UID-t!'); return; }
    setLoading(true); setError('');

    const { data: inserted, error: insErr } = await supabase
      .from('affiliate_requests')
      .insert({ user_id: user?.id || null, email, exchange, exchange_uid: uid.trim(), status: 'pending', source })
      .select('id')
      .single();

    if (insErr) {
      setLoading(false);
      setError(`Hiba: ${insErr.message}`);
      return;
    }

    trackEvent('uid_submitted', { exchange, source }, user);

    // Try automatic verification; any failure → pending
    let approved = false;
    try {
      const { data: verify } = await supabase.functions.invoke('verify-affiliate-uid', {
        body: { request_id: inserted?.id },
      });
      approved = verify?.status === 'approved';
      setIndividual(verify?.review === 'individual');
    } catch { approved = false; }

    setLoading(false);

    if (approved) {
      trackEvent('activation_approved', { source }, user);
      // Let the host app refresh access/tier state and admit the user immediately
      onActivated?.();
      setPhase('approved');
    } else {
      setSubmittedAt(new Date().toISOString());
      setPhase('pending');
    }
  };

  const overlay = {
    position: 'fixed', inset: 0, zIndex: 10000,
    background: 'rgba(3,6,15,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    padding: '40px 16px', overflowY: 'auto',
  };
  const card = {
    width: '100%', maxWidth: 460, position: 'relative',
    background: 'linear-gradient(180deg, rgba(20,26,44,0.98), rgba(10,15,30,0.98))',
    border: '1px solid rgba(140,80,255,0.25)', borderRadius: 20,
    boxShadow: `0 0 60px rgba(0,210,200,0.12)`, padding: '28px 26px',
    fontFamily: 'Inter, system-ui, sans-serif', color: '#fff',
  };
  const input = {
    width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, color: '#fff',
    padding: '11px 14px', fontSize: 14, fontFamily: 'inherit', outline: 'none', marginTop: 6,
  };
  const label = { display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontWeight: 500 };

  return (
    <div style={overlay} onClick={onClose}>
      <style>{`
        @keyframes tamPop { from { opacity: 0; transform: translateY(12px) scale(.98); } to { opacity: 1; transform: none; } }
        @keyframes tamConfetti { 0% { transform: translateY(-10px) rotate(0); opacity: 1; } 100% { transform: translateY(320px) rotate(360deg); opacity: 0; } }
      `}</style>

      <div style={{ ...card, animation: 'tamPop .25s ease both' }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Bezárás" style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 22, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit' }}>×</button>

        {phase === 'checking' ? (
          <div style={{ padding: '36px 0', textAlign: 'center', fontSize: 14, color: 'rgba(255,255,255,0.45)' }}>Betöltés…</div>
        ) : phase === 'approved' ? (
          <ApprovedScreen onClose={onClose} />
        ) : phase === 'pending' ? (
          <PendingScreen onClose={onClose} submittedAt={submittedAt} individual={individual} />
        ) : (
          <>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 12px', color: '#fff', lineHeight: 1.2 }}>
              ⚡ Aktiváld a TAG hozzáférésed — ingyen
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
              {['✓ Kripto Start Tanfolyam', '✓ Discord közösség', '✓ Heti hírlevél'].map(t => (
                <span key={t} style={{ fontSize: 13, color: CYAN, fontWeight: 500 }}>{t}</span>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {PROMO_EXCHANGES.map(ex => (
                <a
                  key={ex.name}
                  href={ex.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('exchange_link_clicked', { exchange: ex.name, source }, user)}
                  style={{ textDecoration: 'none', background: 'rgba(255,255,255,0.03)', border: `1px solid ${ex.color}44`, borderRadius: 12, padding: '12px 14px', display: 'block' }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>{ex.emoji}</span>{ex.name}
                  </div>
                  <div style={{ fontSize: 12, color: ex.color, fontWeight: 600, marginTop: 4 }}>Regisztrálok →</div>
                </a>
              ))}
            </div>

            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: -8, marginBottom: 20, textAlign: 'center' }}>
              MiCA-engedélyes EU partnertőzsdék.{' '}
              <a
                href={BONUSZ_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('bonusz_link_clicked', { source }, user)}
                style={{ color: CYAN, textDecoration: 'none', fontWeight: 600 }}
              >
                Aktuális bónuszok →
              </a>
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={label}>Tőzsde</label>
              <select value={exchange} onChange={e => { setExchange(e.target.value); setError(''); }} style={input}>
                <option value="" style={{ background: '#0a0f1e' }}>— Válassz —</option>
                {EXCHANGES.map(ex => <option key={ex.id} value={ex.id} style={{ background: '#0a0f1e' }}>{ex.label}</option>)}
              </select>
              <button
                onClick={() => setShowUidHelp(v => !v)}
                style={{ background: 'none', border: 'none', color: 'rgba(0,210,200,0.75)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: '8px 0 0' }}
              >
                {showUidHelp ? '▾' : '▸'} Hol találom a UID-m?
              </button>
              {showUidHelp && (
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
                  {UID_GUIDE[exchange] || 'Válassz tőzsdét a fenti listából a UID megtalálásához szükséges útmutatóért. A UID általában a profil / fiókadatok oldalon található egyedi azonosító.'}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={label}>UID / Felhasználónév</label>
              <input
                type="text" value={uid}
                onChange={e => { setUid(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && submit()}
                placeholder="pl. 12345678" style={input}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={label}>Email</label>
              <input type="email" value={email} readOnly style={{ ...input, color: 'rgba(255,255,255,0.4)', cursor: 'default' }} />
            </div>

            {error && (
              <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12, padding: '10px 14px', background: 'rgba(248,113,113,0.08)', borderRadius: 10, border: '1px solid rgba(248,113,113,0.15)' }}>
                {error}
              </div>
            )}

            <button
              onClick={submit} disabled={loading}
              style={{ width: '100%', background: CYAN, color: '#0a0f1e', border: 'none', borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Ellenőrzés...' : '⚡ Aktiválást kérek →'}
            </button>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 12, textAlign: 'center' }}>
              🔒 Adataidat csak az aktiváláshoz használjuk.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function ApprovedScreen({ onClose }) {
  return (
    <div style={{ textAlign: 'center', position: 'relative', paddingTop: 8 }}>
      {/* lightweight confetti */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} style={{
            position: 'absolute', top: -10, left: `${(i * 7) % 100}%`,
            width: 8, height: 8, borderRadius: 2,
            background: [CYAN, PURPLE, '#f59e0b', '#22c55e'][i % 4],
            animation: `tamConfetti ${1.6 + (i % 5) * 0.25}s ease-in ${(i % 6) * 0.1}s forwards`,
          }} />
        ))}
      </div>
      <div style={{ fontSize: 48, marginBottom: 8 }}>🎉</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: CYAN }}>TAG hozzáférésed AKTÍV!</h2>
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', margin: '0 0 12px', lineHeight: 1.6 }}>
        Mostantól elérhető a Kripto Start Tanfolyam, a Discord közösség és a heti hírlevél.
      </p>
      {/* This modal only ever opens from the terminal. TAG does not unlock the
          PRO tabs (utils/access.js: TAG counts as FREE) — say so. */}
      <p style={{ fontSize: 14, color: '#fbbf24', margin: '0 0 22px', lineHeight: 1.6, fontWeight: 600 }}>
        A tanfolyamhoz hozzáférsz. A terminál alapfunkciói ingyenesek; a PRO-funkciókhoz PRO-előfizetés kell.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <a href={COURSE_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', background: CYAN, color: '#0a0f1e', borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 700 }}>
          Tanfolyam megnyitása →
        </a>
        <a href={DISCORD_LINK} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', background: 'rgba(140,80,255,0.15)', color: '#A97BFF', border: '1px solid rgba(140,80,255,0.4)', borderRadius: 100, padding: '13px', fontSize: 15, fontWeight: 700 }}>
          Discord belépés →
        </a>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>
          Bezárás
        </button>
      </div>
    </div>
  );
}

function huDate(iso) {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
}

function PendingScreen({ onClose, submittedAt, individual = false }) {
  const date = huDate(submittedAt);
  return (
    <div style={{ textAlign: 'center', paddingTop: 8 }}>
      <div style={{ fontSize: 44, marginBottom: 10 }}>⏳</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 10px', color: '#F7A600' }}>A kérésed feldolgozás alatt</h2>
      {date && (
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', margin: '0 0 10px' }}>Beküldve: {date}</p>
      )}
      <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', margin: '0 0 22px', lineHeight: 1.65 }}>
        {individual
          ? 'A kérésedet egyedileg bíráljuk el.'
          : <>A tőzsdei regisztrációt ellenőrizzük; általában {APPROVAL_DAYS} napon belül jóváhagyjuk, és e-mailben szólunk, amint aktív a TAG-hozzáférésed. Új kérést nem kell beküldened.</>}
      </p>
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 100, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
        Rendben
      </button>
    </div>
  );
}
